import { computed, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { isUUID } from 'class-validator';
import { useStore } from 'vuex';
import { camelCase, get, last, trim, isString } from 'lodash';
import { DateTime } from 'luxon';
import { useAuth, useMixpanel, useEventHub } from '@/composables';
import {
  AssetVisitType,
  SpotType,
  SpotStatus,
  getSpotStatus,
  LuxonDateTimeFormats,
  isSpotAssignedToAssetVisit,
  isSpotAssignedToAssetContainer,
  isSpotAssignedOnlyToAssetContainer,
  isSpotAssignedOnlyToAssetVisit,
  getAssetContainerStatus,
  getWaitingAssetVisits,
  SpotEventTypeEnum,
  canUserCreateSpotAssignment,
  formatDateTimeWithMilitarySupport,
  YARD_SPOT_CODE_MAX_LENGTH,
  AppointmentStatus,
  sortSpots,
  LoadTypeDirection,
  AssetContainerEventType,
  isFeatureEnabledForWarehouse,
  WarehouseFeatureEnum,
  YardViewModeEnum,
  AssetContainerState,
  canUserCreateSpot,
  isUserRoleGreaterOrEqual,
  UserRole,
  isUserRoleLessOrEqual
} from '@nova/core';
import yardSpotService from '@satellite/services/yardSpot-service';
import { useI18n } from 'vue-i18n';

export default function useYard() {
  const { t, locale } = useI18n();
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const mixpanel = useMixpanel();

  // Event payload: { assetVisit: Object, newSpotCode: string, newSpotAreaName: string }
  const SPOT_ASSIGNMENT_CHANGED = 'spot-assignment-changed';

  // Event payload: { newSpotCode: string }
  const GO_TO_SPOT = 'go-to-spot';

  const storePath = 'state.Yard';
  const spotCardWidth = 60;
  const spotCardHeight = 170;
  const getSpotIcon = type => {
    return type === SpotType.DOCKING ? 'mdi-truck-outline' : 'mdi-parking';
  };

  const getTruckIcon = type => {
    if (type === AssetVisitType.PickUp) {
      return 'mdi-truck-flatbed';
    }

    if (type === AssetVisitType.Drop || type === AssetVisitType.DropHook) {
      return 'mdi-truck-cargo-container';
    }

    return 'mdi-truck-outline';
  };

  const isYMSEnabledForSelectedWarehouse = computed(
    () =>
      Boolean(selectedWarehouse.value?.id) &&
      isFeatureEnabledForWarehouse(selectedWarehouse.value, WarehouseFeatureEnum.YMS)
  );

  const isGateManagementEnabledForSelectedWarehouse = computed(
    () =>
      Boolean(selectedWarehouse.value?.id) &&
      isFeatureEnabledForWarehouse(selectedWarehouse.value, WarehouseFeatureEnum.GateManagement)
  );

  const getAppointmentSpot = appointmentId => {
    if (appointmentId) {
      const spot = spots.value.find(spot =>
        spot.spotAssignments.find(sa => sa.assetVisit?.appointmentId === appointmentId)
      );

      if (spot) {
        spot.spotArea = spotAreas.value.find(sa => sa.id === spot.spotAreaId);

        return spot;
      }
    }

    return null;
  };

  const getAssetVisitSpot = assetVisitId => {
    if (assetVisitId) {
      const spot = spots.value.find(spot =>
        spot.spotAssignments.find(sa => sa.assetVisitId === assetVisitId)
      );

      if (spot) {
        spot.spotArea = spotAreas.value.find(sa => sa.id === spot.spotAreaId);
        return spot;
      }
    }
    return null;
  };

  const getArrivedAssetContainerCompanyName = assetContainer => {
    const arrivedEvent = getArrivedAssetContainerEvent(assetContainer);

    let companyName = t('yard.messages.noVisitFound');

    if (arrivedEvent?.assetVisit?.id) {
      companyName =
        arrivedEvent.assetVisit.company?.name || arrivedEvent.assetVisit.companyHint || ' - ';
    } else if (assetContainer?.dropAppointment?.id) {
      companyName = assetContainer.dropAppointment?.user?.company?.name || ' - ';
    }

    return companyName;
  };

  const getArrivedAssetContainerEvent = assetContainer => {
    if (!assetContainer?.assetContainerEvents?.length) {
      return ' - ';
    }

    return assetContainer.assetContainerEvents.find(
      e => e?.eventType === AssetContainerEventType.Arrived
    );
  };

  /* Get the value of a field from both primary and secondary appointment, if available
   * and join them with a comma
   */
  const getAppointmentFieldValue = (assetVisit, fieldName) => {
    const values = [
      assetVisit?.appointment?.[fieldName],
      assetVisit?.secondaryAppointment?.[fieldName]
    ].filter(v => isString(v) && trim(v) !== '');

    return values.length ? values.map(trim).join(', ') : null;
  };

  const formatHour = value =>
    formatDateTime(
      value,
      LuxonDateTimeFormats.Extended12HrTimeAMPM,
      LuxonDateTimeFormats.Extended24HrTime
    );

  const scheduledFor = appointment => {
    try {
      return Boolean(selectedWarehouse.value?.id) &&
        Boolean(appointment?.id) &&
        Boolean(appointment?.start) &&
        Boolean(appointment?.end)
        ? {
            date: formatDateTime(
              appointment?.start,
              LuxonDateTimeFormats.DateWithAbbreviatedMonth,
              LuxonDateTimeFormats.DateWithAbbreviatedMonth
            ),
            start: formatHour(appointment?.start),
            end: formatHour(appointment?.end)
          }
        : {};
    } catch (error) {
      console.warn('Error while formatting appointment', error);
      return {};
    }
  };

  const getAssignedAssetContainerStatus = assetContainerEvents =>
    getAssetContainerStatus(assetContainerEvents);

  const getAssetContainerStatusColor = status => {
    if (
      status === AssetContainerEventType.LoadingComplete ||
      status === AssetContainerEventType.Loading
    ) {
      return 'warning';
    }

    if (
      status === AssetContainerEventType.Unloading ||
      status === AssetContainerEventType.UnloadingComplete
    ) {
      return 'info';
    }

    if (status === AssetContainerEventType.ReadyForPickup) {
      return 'success';
    }

    if (status === AssetContainerEventType.Inoperable) {
      return 'error';
    }

    return 'text--color-accent-60';
  };

  const getAssetContainerStatusIcon = status => {
    if (
      status === AssetContainerEventType.LoadingComplete ||
      status === AssetContainerEventType.UnloadingComplete
    ) {
      return ' mdi-check-bold';
    }

    if (status === AssetContainerEventType.Loading) {
      return 'mdi-transfer-down';
    }

    if (status === AssetContainerEventType.Unloading) {
      return 'mdi-transfer-up';
    }

    if (status === AssetContainerEventType.ReadyForPickup) {
      return 'mdi-check-circle';
    }

    if (status === AssetContainerEventType.Departed) {
      return 'mdi-truck-fast-outline';
    }

    if (status === AssetContainerEventType.Inoperable) {
      return 'mdi-alert-octagon';
    }

    return 'mdi-progress-clock';
  };

  const getAssetContainerStateIcon = state => {
    if (state === AssetContainerState.Empty) {
      return 'mdi-circle-outline';
    }
    if (state === AssetContainerState.Full) {
      return 'mdi-circle';
    }
    if (state === AssetContainerState.PartiallyLoaded) {
      return 'mdi-circle-half-full';
    }
  };

  const isAssetContainer = asset => Boolean(asset.code) && Boolean(asset.type) && !asset.visitType;

  const getAssignedSpotIcon = loadtypeDirection => {
    if (loadtypeDirection) {
      if (loadtypeDirection === LoadTypeDirection.Inbound) {
        return 'mdi-menu-up';
      }

      if (loadtypeDirection === LoadTypeDirection.Outbound) {
        return 'mdi-menu-down';
      }

      if (loadtypeDirection === LoadTypeDirection.InOut) {
        return 'mdi-menu-swap';
      }
    }

    return null;
  };

  const getLoadTypeDirectionColor = loadTypeDirection => {
    if (loadTypeDirection === LoadTypeDirection.Inbound) {
      return 'info';
    }
    if (loadTypeDirection === LoadTypeDirection.Outbound) {
      return 'warning';
    }

    return 'text--color-black';
  };

  const relativeTimeFromWarehouse = date => {
    const zone = selectedWarehouse.value.timezone;
    return DateTime.fromISO(date, { zone })
      .setLocale(locale.value)
      .toRelative({ base: DateTime.now().setZone(zone) })
      .replace('ago', '');
  };

  const timeElapsedFromWarehouse = date => {
    if (!date) return '';

    const zone = selectedWarehouse.value.timezone;
    const start = DateTime.fromISO(date, { zone });
    const now = DateTime.now().setZone(zone);

    const diff = now.diff(start);
    if (diff.as('minutes') < 1) {
      return '00:01';
    }
    return diff.toFormat('hh:mm');
  };

  const MixPanelEvent = Object.freeze({
    VIEW_PAGE: 'Viewed: Page',
    MESSAGE_DRIVER: 'Action: SMS Message Sent',
    CREATE_ASSET_VISIT: 'Created: Asset Visit',
    CREATE_ASSET_CONTAINER: 'Created: Asset Container',
    UPDATE_ASSET_CONTAINER_STATUS: 'Updated: Asset Container Status',
    UPDATE_ASSET_CONTAINER: 'Updated: Asset Container',
    LINK_ASSET_VISIT_APPOINTMENT: 'Linked: Asset Visit Appointment',
    UNLINK_ASSET_VISIT_APPOINTMENT: 'Unlinked: Asset Visit Appointment',
    UNLINK_ASSET_CONTAINER_APPOINTMENT: 'Unlinked: Asset Container Appointment',
    LINK_ASSET_CONTAINER_APPOINTMENT: 'Linked: Asset Container Appointment',
    SWITCH_VIEW_MODE: 'Switched: Live Yard View Mode',
    UPDATE_FORM_FIELD: 'Updated: Form Field',
    GO_TO_SPOT: 'Clicked: Go to Spot',
    DROP: 'Dropped: Asset Container',
    HOOK: 'Hooked: Asset Container',
    DEPART: 'Departed: Asset Visit',
    ASSIGNED: 'Assigned: Asset to Spot',
    UNASSIGNED: 'Unassigned: Asset from Spot',
    REASSIGN: 'Reassigned: Asset to Spot',
    CREATE_AREA: 'Created: Yard Area',
    DELETE_AREA: 'Deleted: Yard Area',
    UPDATE_AREA: 'Updated: Yard Area',
    CREATE_SPOT: 'Added: Area Spot',
    DELETE_SPOT: 'Deleted: Area Spot',
    UPDATE_SPOT: 'Updated: Area Spot',
    CLOSE_SPOT: 'Closed: Area Spot',
    OPEN_SPOT: 'Reopened: Area Spot',
    EXPORT: 'Exported: Yard Assets'
  });

  /*
   * Track mixpanel event for YMS module
   * @param {String} event - Mixpanel event name, check mixPanelEvent for available events
   * @param {Object} spot - Spot object
   * @param {Object} assetVisit - AssetVisit object
   * @param {String} entryPoint - Entry point
   * @param {Object} metadata - Additional metadata, as object
   * @returns {void}
   * */
  const trackMixpanelEvent = (event, { spot, assetVisit, entryPoint, spotArea, metadata }) => {
    try {
      const loggedUser = store.getters['Auth/me'];
      const localSpotArea = spotAreas.value.find(sa => sa.id === spot?.spotAreaId);
      const eventData = {
        org_id: loggedUser?.orgId,
        org_name: loggedUser?.orgName,
        warehouse_id: selectedWarehouse.value?.id,
        warehouse_name: selectedWarehouse.value?.name,
        area_id: (spotArea ?? localSpotArea)?.id,
        area_name: (spotArea ?? localSpotArea)?.name,
        spot_id: spot?.id,
        spot_code: spot?.code,
        spot_type: spot?.type,
        asset_visit_id: assetVisit && !isAssetContainer(assetVisit) ? assetVisit?.id : null,
        asset_visit_type: assetVisit?.visitType,
        asset_container_id: assetVisit && isAssetContainer(assetVisit) ? assetVisit?.id : null,
        asset_container_type: assetVisit && isAssetContainer(assetVisit) ? assetVisit?.type : null,
        checkin_type: assetVisit
          ? assetVisit?.isPlanned
            ? 'Planned Arrival'
            : 'Unplanned Arrival'
          : null,
        entry_point: entryPoint,
        // eslint-disable-next-line no-unused-vars
        ...(metadata && Object.fromEntries(Object.entries(metadata).filter(([_, v]) => v != null)))
      };

      mixpanel.track(
        event,
        // eslint-disable-next-line no-unused-vars
        Object.fromEntries(Object.entries(eventData).filter(([_, v]) => v != null))
      );
    } catch (err) {
      console.warn(`Failed to track mixpanel event ${event}`, err);
    }
  };

  const goToSpotFromOtherModules = async ({ spotId, spotCode, warehouseId }) => {
    if (!spotId || !spotCode || !warehouseId) {
      throw new Error('Invalid parameters for goToSpotFromOtherModules');
    }

    return router
      .push({
        name: 'yard-warehouse',
        params: {
          warehouseId
        }
      })
      .then(() => {
        // Wait for spots and visits to load before emitting the event
        let maxChecks = 5;
        const { $emit } = useEventHub();
        const checkLoading = () => {
          if (maxChecks-- <= 0) {
            throw new Error('Timeout waiting for spots and visits to load');
          }

          if (
            !isLoadingSpots.value &&
            !isLoadingAssetVisits.value &&
            selectedAreas.value.length > 0 &&
            spots.value.length > 0
          ) {
            const spot = spots.value.find(spot => spot.id === spotId);
            selectedSpot.value = spot;

            viewMode.value = YardViewModeEnum.GRID;

            trackMixpanelEvent(MixPanelEvent.GO_TO_SPOT, {
              spot: { id: spotId, code: spotCode },
              entryPoint: 'Appointment Details'
            });

            $emit(GO_TO_SPOT, {
              newSpotCode: spotCode
            });
          } else {
            setTimeout(checkLoading, 400);
          }
        };
        checkLoading();
      });
  };

  const truckThemeColors = {
    blue: {
      backgroundColor: '#E4F1F7',
      borderColor: '#005AD8'
    },
    red: {
      backgroundColor: '#FAEBEE',
      borderColor: '#D40047'
    },
    grey: {
      backgroundColor: '#F2F2F2',
      borderColor: '#656565'
    }
  };

  const getTruckThemeColor = assetVisit => {
    const appointment = assetVisit?.appointment;
    const warehouseNow = DateTime.now().setZone(selectedWarehouse.value.timezone);

    if (appointment?.id) {
      if (
        appointment.status == AppointmentStatus.Cancelled ||
        appointment.status == AppointmentStatus.NoShow ||
        // If appointment end time is in the past, we should consider it as cancelled
        warehouseNow > DateTime.fromISO(appointment.end)
      ) {
        return { ...truckThemeColors.red, theme: 'red' };
      }

      // If it is an active appointment and not ended yet, show as blue
      return { ...truckThemeColors.blue, theme: 'blue' };
    }

    // When no appointment, show as grey
    return { ...truckThemeColors.grey, theme: 'grey' };
  };

  const isEditableSpotSelected = spot => {
    if (selectedSpots?.value?.length >= 1) {
      return Boolean(selectedSpots.value.find(s => s.id === spot.id)?.id);
    }

    return selectedSpot.value?.id === spot.id;
  };

  const isMilitaryTimeEnabled = computed(() =>
    store.getters['Settings/isMilitaryTimeEnabled']({
      ...selectedWarehouse.value
    })
  );

  const getEvents = async spotId => {
    const events = await yardSpotService.getEvents(spotId);

    return Array.isArray(events) ? events : [];
  };

  const getLatestClosedEvent = async spotId => {
    // TODO: Filter by eventType perhaps and sort by createdAt
    const events = await getEvents(spotId);

    if (events.length > 0) {
      const closeEvents = events
        .filter(event => event.eventType === SpotEventTypeEnum.CLOSE)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      return last(closeEvents) || {};
    }

    return {};
  };

  const getContainerSpotAssignmentText = assetContainerId => {
    const spot = spots.value.find(spot =>
      spot.spotAssignments?.find(
        sa =>
          sa.assetContainerId === assetContainerId ||
          sa.assetVisit?.assetContainers?.find(ac => ac.id === assetContainerId)
      )
    );

    if (!spot) {
      return {
        spotAreaName: null,
        spotCode: null,
        spotId: null
      };
    }

    const spotArea = spotsByAreaMap.value.get(spot.spotAreaId)?.area ?? null;

    return {
      spotAreaName: spotArea?.name ?? spot.spotAreaName ?? null,
      spotCode: spot.code ?? null,
      spotId: spot.id ?? null
    };
  };

  const getAssetVisitTypeDisplayName = type => {
    if (type === AssetVisitType.Drop) {
      return t('core.yard.assetVisitTypes.dropVisit');
    }
    if (type === AssetVisitType.DropHook) {
      return t('core.yard.assetVisitTypes.dropHook');
    }
    if (type === AssetVisitType.PickUp) {
      return t('core.yard.assetVisitTypes.pickupVisit');
    }
    if (type === AssetVisitType.Live) {
      return t('core.yard.assetVisitTypes.liveLoad');
    }
    return type;
  };

  const isLoadingSpots = computed(() => get(store, `${storePath}.isLoadingSpots`));
  const isLoadingAssetVisits = computed(() => get(store, `${storePath}.isLoadingAssetVisits`));
  const assetContainerTriggers = computed(() => get(store, `${storePath}.assetContainerTriggers`));

  const spotAreas = computed(() => get(store, `${storePath}.spotAreas`));
  const spots = computed(() => get(store, `${storePath}.spots`));
  const assetVisits = computed(() => get(store, `${storePath}.assetVisits`));
  const assetContainers = computed(() => get(store, `${storePath}.assetContainers`));

  const waitingAssetVisits = computed(() => {
    const validAssetVisits = getWaitingAssetVisits(assetVisits.value);
    const assignedAssetVisitIds = new Set(
      spots.value
        .filter(s => s?.spotAssignments?.length > 0)
        .flatMap(s => [
          s.spotAssignments[0]?.assetContainerId,
          s.spotAssignments[0]?.assetVisitId,
          s.spotAssignments[0]?.assetVisit?.assetContainers.flatMap(ac => ac.id)
        ])
    );

    const notAssigned = validAssetVisits.filter(av => !assignedAssetVisitIds.has(av.id));

    return notAssigned;
  });

  // Read-write refs
  const selectedWarehouse = computed({
    get() {
      return get(store, `${storePath}.selectedWarehouse`);
    },
    set(value) {
      store.commit('Yard/setSelectedWarehouse', value || null);

      if (value?.id) {
        // Make sure all related data is fetched
        fetchAssetVisits();
        fetchSpotAreas();
        fetchSpots();
        fetchAssetContainerTriggers();

        // Update warehouseId to the url
        router.push({
          params: { warehouseId: value.id }
        });
      } else {
        // Clean up the url
        router.push({
          name: 'yard'
        });
      }
    }
  });
  const selectedAreas = computed({
    get() {
      return get(store, `${storePath}.selectedAreas`);
    },
    set(value) {
      store.commit('Yard/setSelectedAreas', value || []);
    }
  });
  const selectedSpot = computed({
    get() {
      return get(store, `${storePath}.selectedSpot`);
    },
    set(value) {
      store.commit('Yard/setSelectedSpot', value || {});
      if (value?.id) {
        store
          .dispatch('Yard/fetchSpot', value.id)
          .then(spot => store.commit('Yard/setSelectedSpot', spot));
        selectedSpots.value = [];

        nextTick(() => {
          const element = document.getElementById(value.id);

          if (element) {
            setTimeout(() => {
              element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
            }, 500);
          }
        });
      }
    }
  });
  const selectedAssetVisit = computed({
    get() {
      return get(store, `${storePath}.selectedAssetVisit`);
    },
    set(value) {
      store.commit('Yard/setSelectedAssetVisit', value || {});
    }
  });
  const isAssetVisitChatVisible = computed({
    get() {
      return get(store, `${storePath}.isAssetVisitChatVisible`);
    },
    set(value) {
      store.commit('Yard/setIsAssetVisitChatVisible', value);
    }
  });
  const selectedSpots = computed({
    get() {
      return get(store, `${storePath}.selectedSpots`);
    },
    set(value) {
      store.commit('Yard/setSelectedSpots', value || []);
      if (value?.length > 0) {
        selectedSpot.value = {};
      }
    }
  });
  const viewMode = computed({
    get() {
      return get(store, `${storePath}.viewMode`);
    },
    set(value) {
      store.dispatch('Yard/setViewMode', { value });
    }
  });

  // Route refs
  const defaultWarehouseId = computed(() =>
    isUUID(route.params?.warehouseId) ? String(route.params.warehouseId) : null
  );
  const defaultSpotAreaId = computed(() =>
    isUUID(route.params?.spotAreaId) ? String(route.params.spotAreaId) : null
  );
  const defaultSpotId = computed(() =>
    isUUID(route.params?.spotId) ? String(route.params.spotId) : null
  );

  const auth = useAuth();

  const cannotCreateSpotAssignment = computed(() => {
    const user = auth.meRef.value;
    return !canUserCreateSpotAssignment(user);
  });

  const canManageSpotsAndAreas = computed(() => {
    const user = auth.meRef.value;
    return canUserCreateSpot(user);
  });

  const canEditAssetForms = computed(() => {
    const user = auth.meRef.value;
    return isUserRoleGreaterOrEqual(user.role, UserRole.OPERATOR);
  });

  const canEditAssetContainerData = computed(() => {
    const user = auth.meRef.value;
    return isUserRoleGreaterOrEqual(user.role, UserRole.ATTENDANT);
  });

  const isReadonlyAccess = computed(() => {
    const user = auth.meRef.value;
    return isUserRoleLessOrEqual(user.role, UserRole.SPECTATOR);
  });

  // Actions
  const fakeCheckIn = data => store.dispatch('Yard/fakeCheckIn', data);
  const fakeCheckInUnplanned = data => store.dispatch('Yard/fakeCheckInUnplanned', data);
  const fetchPreviousYardData = () => store.dispatch('Yard/fetchPreviousYardData');
  const persistSelectedWarehouse = warehouseId =>
    store.dispatch('Yard/persistSelectedWarehouse', warehouseId);
  const persistListViewColumns = columns => store.dispatch('Yard/persistListViewColumns', columns);
  const detachAssetVisit = (
    assetVisitId,
    assetVisitAction,
    assetContainerActions,
    reassignData = null
  ) =>
    store.dispatch('Yard/detachAssetVisit', {
      assetVisitId,
      assetVisitAction,
      assetContainerActions,
      reassignData
    });
  const attachAssetVisit = (assetVisitId, assetContainerActions) =>
    store.dispatch('Yard/attachAssetVisit', { assetVisitId, assetContainerActions });
  const updateAssetContainer = (assetContainerId, data) =>
    store.dispatch('Yard/updateAssetContainer', { assetContainerId, data });
  const updateAssetContainerStatus = (assetContainerId, eventType) =>
    store.dispatch('Yard/updateAssetContainerStatus', { assetContainerId, eventType });
  const updateAssetVisitStatus = (assetVisitId, eventType) =>
    store.dispatch('Yard/updateAssetVisitStatus', { assetVisitId, eventType });
  const getAppointmentById = appointmentId =>
    store.dispatch('Appointments/getAppointmentById', appointmentId);
  const fetchAssetVisits = () => store.dispatch('Yard/fetchAssetVisits');
  const fetchAssetContainers = () => store.dispatch('Yard/fetchAssetContainers');
  const fetchAssetContainerTriggers = () => store.dispatch('Yard/fetchAssetContainerTriggers');
  const fetchSpots = () => store.dispatch('Yard/fetchSpots');
  const fetchSpotAreas = () => store.dispatch('Yard/fetchSpotAreas');
  const fetchSpot = id => store.dispatch('Yard/fetchSpot', id);
  const createSpotArea = data => store.dispatch('Yard/createSpotArea', data);
  const updateSpotArea = (id, data) => store.dispatch('Yard/updateSpotArea', { id, data });
  const deleteSpotArea = id => store.dispatch('Yard/deleteSpotArea', id);
  const updateSpot = (id, data) => store.dispatch('Yard/updateSpot', { id, data });
  const createSpot = data => store.dispatch('Yard/createSpot', data);
  const createManySpot = data => store.dispatch('Yard/createManySpot', data);
  const closeSpot = (id, data) => store.dispatch('Yard/closeSpot', { id, data });
  const openSpot = (id, data) => store.dispatch('Yard/openSpot', { id, data });
  const deleteSpot = id => store.dispatch('Yard/deleteSpot', id);
  const deleteManySpot = ids => store.dispatch('Yard/deleteManySpot', ids);
  const createSpotAssignment = data => store.dispatch('Yard/createSpotAssignment', data);
  const deleteSpotAssignment = id => store.dispatch('Yard/deleteSpotAssignment', id);
  const departSpotAssignment = (id, assetType) =>
    store.dispatch('Yard/departSpotAssignment', { id, assetType });
  const updateSpotAssignment = (id, data) =>
    store.dispatch('Yard/updateSpotAssignment', { id, data });
  const reassignSpotAssignment = (id, data) =>
    store.dispatch('Yard/reassignSpotAssignment', { id, data });
  const messageDriver = (assetVisitId, content) =>
    store.dispatch('Yard/messageDriver', { assetVisitId, content });
  const setIsLoadingSpots = value => store.commit('Yard/setIsLoadingSpots', value);
  const setIsLoadingAssetVisits = value => store.commit('Yard/setIsLoadingAssetVisits', value);

  // Subspace
  const showUpdateDataFromSubspace = (event, collection) => {
    return (
      collection?.value?.find(item => item.id === event.id) ||
      event.warehouseId === selectedWarehouse.value?.id
    );
  };
  const handleAssetContainerEventSubspaceEvent = async event => {
    for (const spot of spots.value) {
      const spotAssignment = spot.spotAssignments[0];
      if (
        spotAssignment?.assetContainer?.id === event.assetContainerId ||
        spotAssignment?.assetVisit?.assetContainers?.find(ac => ac.id === event.assetContainerId)
          ?.id
      ) {
        return fetchSpot(spot.id).then(updateSpotOnSpotList);
      }
    }

    return fetchAssetVisits();
  };
  const handleAssetContainerSubspaceEvent = async event => {
    for (const spot of spots.value) {
      const spotAssignment = spot.spotAssignments[0];
      if (
        spotAssignment?.assetContainer?.id === event.id ||
        spotAssignment?.assetVisit?.assetContainers?.find(ac => ac.id === event.id)?.id
      ) {
        return fetchSpot(spot.id).then(updateSpotOnSpotList);
      }
    }

    return fetchAssetVisits();
  };
  const updateSpotOnSpotList = spot => {
    if (!spot?.id) {
      return;
    }

    if (spots.value.map(s => s.id).includes(spot.id)) {
      store.commit(
        'Yard/setSpots',
        spots.value.map(s => (s.id === spot.id ? spot : s))
      );
    } else {
      store.commit('Yard/setSpots', [...spots.value, spot]);
    }

    if (selectedSpot.value?.id === spot.id) {
      store.commit('Yard/setSelectedSpot', spot);
    }
  };
  const handleAssetVisitEventSubspaceEvent = async event => {
    const existingAssetVisit = waitingAssetVisits?.value?.find(av => av.id === event.assetVisitId);

    const assetVisitSpot = spots.value.find(spot =>
      spot.spotAssignments.find(sa => sa.assetVisitId === event.assetVisitId)
    );

    const isNotAssigned = existingAssetVisit && !assetVisitSpot;

    if (isNotAssigned) {
      return fetchAssetVisits();
    }

    if (assetVisitSpot) {
      return fetchSpot(assetVisitSpot.id).then(updateSpotOnSpotList);
    }
  };
  const handleAssetVisitSubspaceEvent = async event => {
    if (!selectedWarehouse.value?.id || event.warehouseId !== selectedWarehouse.value.id) {
      return;
    }

    const assetVisitSpot = spots.value.find(spot =>
      spot.spotAssignments.find(sa => sa.assetVisitId === event.id)
    );

    const sameWarehouse = event.warehouseId === selectedWarehouse.value.id;

    if (assetVisitSpot) {
      return fetchSpot(assetVisitSpot.id).then(updateSpotOnSpotList);
    } else if (sameWarehouse) {
      return fetchAssetVisits();
    }
  };
  const handleSpotAreaSubspaceEvent = async event => {
    if (showUpdateDataFromSubspace(event, spotAreas)) {
      fetchSpotAreas();
    }
  };
  const handleSpotSubspaceEvent = async event => {
    if (event.debounce?.count > 0) {
      return fetchSpots().then(() => {
        if (event.debounce.ids?.includes(selectedSpot.value?.id)) {
          store.commit('Yard/setSelectedSpot', {});
        }
      });
    }

    if (showUpdateDataFromSubspace(event, spots)) {
      if (event.action === 'delete') {
        store.commit(
          'Yard/setSpots',
          spots.value.filter(s => s.id !== event.id)
        );

        if (selectedSpot.value?.id === event.id) {
          store.commit('Yard/setSelectedSpot', {});
        }
        return;
      }
      return fetchSpot(event.id).then(updateSpotOnSpotList);
    }
  };
  const handleSpotAssignmentSubspaceEvent = async event => {
    if (event.debounce?.count > 0) {
      fetchSpots();
      fetchAssetVisits();
      return;
    }

    if (event.action === 'delete') {
      const spotId = event.spotId;

      fetchAssetVisits();
      return fetchSpot(spotId).then(updateSpotOnSpotList);
    }

    const existingSpotAssignment = spots.value.find(spot =>
      spot.spotAssignments.find(sa => sa.id === event.id)
    );
    const newSpotId = event.spotId;

    if (existingSpotAssignment) {
      fetchSpot(existingSpotAssignment.id).then(updateSpotOnSpotList);
    }

    if (newSpotId) {
      fetchSpot(newSpotId).then(updateSpotOnSpotList);
    }
  };
  const handleAppointmentSubspaceEvent = event => {
    for (const spot of spots.value) {
      const spotAssignment = spot.spotAssignments[0];
      if (
        spotAssignment?.assetVisit?.appointmentId === event.id ||
        spotAssignment?.assetVisit?.assetContainers?.find(
          ac => ac.pickupAppointment?.id === event.id
        )?.id ||
        spotAssignment?.assetVisit?.assetContainers?.find(ac => ac.dropAppointment?.id === event.id)
          ?.id ||
        spotAssignment?.assetContainer?.pickupAppointment?.id === event.id ||
        spotAssignment?.assetContainer?.dropAppointment?.id === event.id
      ) {
        fetchSpot(spot.id).then(updateSpotOnSpotList);
      }
    }

    for (const assetVisit of waitingAssetVisits.value) {
      if (
        assetVisit.appointmentId === event.id ||
        assetVisit.pickupAppointment?.id === event.id ||
        assetVisit.dropAppointment?.id === event.id ||
        assetVisit.assetContainers?.find(ac => ac.pickupAppointment?.id === event.id)?.id ||
        assetVisit.assetContainers?.find(ac => ac.dropAppointment?.id === event.id)?.id
      ) {
        // TODO: Not very nice, we should fetch only the asset visit in question
        fetchAssetVisits().then(() => {
          if (selectedAssetVisit.value?.id === assetVisit.id) {
            selectedAssetVisit.value = waitingAssetVisits.value.find(av => av.id === assetVisit.id);
          }
        });
        break;
      }
    }
  };

  const validateSpotCodeInput = value => {
    if (
      value?.length < 1 ||
      value?.length > YARD_SPOT_CODE_MAX_LENGTH ||
      !/^[a-zA-Z0-9]*$/.test(value)
    ) {
      return `Code must be between 1 and ${YARD_SPOT_CODE_MAX_LENGTH} characters and contains only letters and numbers (a-z,  0-9)`;
    }
    return true;
  };

  const validateSpotTypeInput = value => {
    if (!value) {
      return 'Spot type is required';
    }

    if (!Object.values(SpotType).includes(value)) {
      return `Spot type is not valid, valid choices are ${Object.values(SpotType).join(',')}`;
    }
    return true;
  };

  const formatDateTime = (date, amPmFormat, militaryFormat) =>
    formatDateTimeWithMilitarySupport(
      date,
      selectedWarehouse.value?.timezone,
      amPmFormat,
      isMilitaryTimeEnabled.value,
      militaryFormat,
      locale.value
    );

  const getTranslatedCompanyName = companyName => {
    return companyName ?? t('yard.messages.unknownCompany');
  };

  const getTranslatedAssetContainerType = type => {
    return t(`core.yard.assetContainerTypes.${camelCase(type)}`);
  };

  const getTranslatedSpotStatus = spot => {
    const status = getSpotStatus(spot);
    return t(`core.yard.spotStatuses.${camelCase(status)}`);
  };

  const spotsByAreaMap = computed(() => {
    const spotsByArea = new Map();

    spotAreas.value.forEach(area => {
      spotsByArea.set(area.id, {
        area,
        spots: []
      });
    });

    spots.value.forEach(spot => {
      if (!spot.isActive) {
        return;
      }

      const areaData = spotsByArea.get(spot.spotAreaId);
      if (areaData) {
        areaData.spots.push(spot);
      } else {
        console.warn('Could not find area', spot.spotAreaId, { spot: JSON.stringify(spot) });
      }
    });

    return spotsByArea;
  });

  const getSpotsByArea = areaId => {
    return spotsByAreaMap.value.get(areaId)?.spots || [];
  };

  const isSpotOpen = spot => getSpotStatus(spot) === SpotStatus.OPEN;
  const isSpotClosed = spot => getSpotStatus(spot) === SpotStatus.CLOSED;
  const isSpotAssigned = spot => getSpotStatus(spot) === SpotStatus.ASSIGNED;
  const isSpotReserved = spot => getSpotStatus(spot) === SpotStatus.RESERVED;

  /**
   * TODO: evaluate if we need to increase the granularity of this getter
   * to return if the spot is the origin or destination of an active move task,
   * or the actual active move task itself.
   *
   * If we do, we need to update the `useYardSpotStatus` composable to use the new getter,
   * or remove it altogether.
   */
  const isSpotInActiveMoveTask = spotId => {
    return store.getters['Yard/isSpotInActiveMoveTask'](spotId);
  };

  const hasAssignedAssetContainer = spot => {
    const toArray = value => (Array.isArray(value) ? value : []);
    const spotAssignments = toArray(spot?.spotAssignments);

    return spotAssignments.some(spotAssignment => {
      if (spotAssignment.assetContainer?.id) {
        return true;
      }

      if (toArray(spotAssignment.assetVisit?.assetContainers).some(ac => ac.id)) {
        return true;
      }
      return false;
    });
  };

  const doesSpotHaveLockedAsset = spot => {
    /**
     * We check this because, the origin spot for a move task - which is causes
     * an asset to be "locked" - is currently being set as closed (i.e., isActive = false).
     */
    if (!isSpotClosed(spot)) {
      return false;
    }

    return hasAssignedAssetContainer(spot);
  };

  return {
    // Read-Write Refs
    selectedWarehouseRef: selectedWarehouse,
    selectedAreasRef: selectedAreas,
    selectedSpotRef: selectedSpot,
    selectedAssetVisitRef: selectedAssetVisit,
    selectedSpotsRef: selectedSpots,
    viewModeRef: viewMode,
    isAssetVisitChatVisibleRef: isAssetVisitChatVisible,
    // Read-Only Refs
    assetContainerTriggersRef: assetContainerTriggers,
    spotAreasRef: spotAreas,
    spotsRef: spots,
    assetContainersRef: assetContainers,
    isLoadingSpotsRef: isLoadingSpots,
    isLoadingAssetVisitsRef: isLoadingAssetVisits,
    defaultWarehouseIdRef: defaultWarehouseId,
    defaultSpotAreaIdRef: defaultSpotAreaId,
    defaultSpotIdRef: defaultSpotId,
    waitingAssetVisitsRef: waitingAssetVisits,
    // Methods
    fakeCheckIn,
    fakeCheckInUnplanned,
    attachAssetVisit,
    detachAssetVisit,
    updateAssetContainerStatus,
    updateAssetVisitStatus,
    getAppointmentById,
    getAssetVisitSpot,
    fetchSpots,
    fetchSpotAreas,
    fetchAssetVisits,
    fetchAssetContainers,
    createSpotArea,
    updateSpotArea,
    deleteSpotArea,
    getArrivedAssetContainerEvent,
    getArrivedAssetContainerCompanyName,
    handleAssetVisitEventSubspaceEvent,
    handleAssetVisitSubspaceEvent,
    handleSpotAreaSubspaceEvent,
    handleSpotSubspaceEvent,
    handleAssetContainerSubspaceEvent,
    handleAssetContainerEventSubspaceEvent,
    getAppointmentSpot,
    createSpot,
    createManySpot,
    closeSpot,
    openSpot,
    deleteSpot,
    deleteManySpot,
    fetchSpot,
    updateSpot,
    messageDriver,
    fetchPreviousYardData,
    fetchAssetContainerTriggers,
    createSpotAssignment,
    deleteSpotAssignment,
    departSpotAssignment,
    reassignSpotAssignment,
    updateSpotAssignment,
    validateSpotCodeInput,
    validateSpotTypeInput,
    handleAppointmentSubspaceEvent,
    handleSpotAssignmentSubspaceEvent,
    getAssetVisitTypeDisplayName,
    formatDateTime,
    getTruckThemeColor,
    setIsLoadingSpots,
    isEditableSpotSelected,
    isReadonlyAccess,
    sortSpots,
    relativeTimeFromWarehouse,
    getAssignedAssetContainerStatus,
    setIsLoadingAssetVisits,
    updateAssetContainer,
    isAssetContainer,
    updateSpotOnSpotList,
    trackMixpanelEvent,
    scheduledFor,
    MixPanelEvent,
    isSpotOpen,
    isSpotClosed,
    isSpotAssigned,
    isSpotReserved,
    isSpotInActiveMoveTask,
    hasAssetVisitOnSpot: spot => isSpotAssignedToAssetVisit(spot),
    hasAssetContainerOnSpot: spot => isSpotAssignedToAssetContainer(spot),
    hasAssetVisitOnlyOnSpot: spot => isSpotAssignedOnlyToAssetVisit(spot),
    hasAssetContainerOnlyOnSpot: spot => isSpotAssignedOnlyToAssetContainer(spot),
    // Basic Data
    spotCardHeight,
    spotCardWidth,
    spotType: SpotType,
    spotStatus: SpotStatus,
    persistSelectedWarehouse,
    persistListViewColumns,
    getAssignedSpotIcon,
    getAssetContainerStateIcon,
    getAssetContainerStatusIcon,
    getAssetContainerStatusColor,
    getSpotIcon,
    getTruckIcon,
    getLoadTypeDirectionColor,
    getContainerSpotAssignmentText,
    getAppointmentFieldValue,
    timeElapsedFromWarehouse,
    isYMSEnabledForSelectedWarehouse,
    isGateManagementEnabledForSelectedWarehouse,
    getEvents,
    getLatestClosedEvent,
    doesSpotHaveLockedAsset,
    cannotCreateSpotAssignmentRef: cannotCreateSpotAssignment,
    canManageSpotsAndAreasRef: canManageSpotsAndAreas,
    canEditAssetFormsRef: canEditAssetForms,
    canEditAssetContainerDataRef: canEditAssetContainerData,
    getSpotStatus: getSpotStatus,
    isMilitaryTimeEnabled,
    goToSpotFromOtherModules,
    truckThemeColors,
    YARD_SPOT_CODE_MAX_LENGTH,
    SPOT_ASSIGNMENT_CHANGED,
    GO_TO_SPOT,
    getTranslatedCompanyName,
    getTranslatedAssetContainerType,
    getTranslatedSpotStatus,
    spotsByAreaMap,
    getSpotsByArea
  };
}
